import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { HomeHeader } from '../home/HomeHeader'
import BookList from './BookList'
import { GradedBooksSection } from './GradedBooksSection'
import { TakenBooksSection } from './TakenBooksSection'
import { Search as SearchComponent, type SortOption } from '../shared/Search'
import { Pagination, type PaginationData } from '../shared/Pagination'
import { Book } from '../../types'
import { apiClient } from '../../lib/api'
import { Button } from '../ui/button'
import { ALL_LANGUAGES } from '../../lib/constants/languages'
import { useTranslation } from 'react-i18next'

/**
 * Calculate top language codes from user books.
 * Counts languages from:
 * 1. Original languages of books (if available)
 * 2. Available translation languages (if available)
 * 
 * Returns language codes sorted by frequency (most common first).
 * Returns empty array if no languages found (no books or no languages in books).
 */
function calculateTopLanguagesFromBooks(books: Book[]): string[] {
  if (books.length === 0) {
    return []
  }
  
  const languageCounts: Record<string, number> = {}
  
  books.forEach(book => {
    // Count original language (handle both Book type and API response)
    // original_language is already a language code (e.g., "en", "ru")
    const originalLang = (book as any).original_language
    if (originalLang && typeof originalLang === 'string') {
      languageCounts[originalLang] = (languageCounts[originalLang] || 0) + 1
    }
    
    // Count available translation languages (handle both Book type and API response)
    // available_languages is an array of language codes (e.g., ["en", "nb", "es"])
    const availableLangs = (book as any).available_languages
    if (availableLangs && Array.isArray(availableLangs)) {
      availableLangs.forEach((code: string) => {
        if (code && typeof code === 'string') {
          languageCounts[code] = (languageCounts[code] || 0) + 1
        }
      })
    }
  })
  
  // Sort by count (descending) and return codes
  // Return empty array if no languages found
  return Object.entries(languageCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([code]) => code)
}

export default function Dashboard() {
  const { t } = useTranslation('common')
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [books, setBooks] = useState<Book[]>([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationData | null>(null)
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [languageFilter, setLanguageFilter] = useState<string>(searchParams.get('language') || '')
  const [genreFilter, setGenreFilter] = useState(searchParams.get('genre') || '')
  const [originalLanguageFilter, setOriginalLanguageFilter] = useState(searchParams.get('original_language') || '')
  const [sortFilter, setSortFilter] = useState<SortOption | ''>(searchParams.get('sort') as SortOption | '' || '')

  // Calculate top languages from loaded books
  const topLanguageCodes = useMemo(() => {
    return calculateTopLanguagesFromBooks(books)
  }, [books])

  // Separate languages for "translated to" (only available_languages) vs "original in" (only original_languages)
  const translatedToLanguages = useMemo(() => {
    if (books.length === 0) return [] // Empty array means show no languages
    
    const relevantCodes = new Set<string>()
    books.forEach(book => {
      // available_languages is an array of language codes (e.g., ["en", "nb", "es"])
      const availableLangs = (book as any).available_languages || []
      availableLangs.forEach((code: string) => {
        if (code && typeof code === 'string') {
          relevantCodes.add(code)
        }
      })
    })
    
    if (relevantCodes.size === 0) return [] // No translations available, return empty array (show nothing)
    
    return ALL_LANGUAGES.filter((lang: { code: string }) => relevantCodes.has(lang.code))
  }, [books])
  
  const originalLanguages = useMemo(() => {
    if (books.length === 0) return [] // Empty array means show no languages
    
    const relevantCodes = new Set<string>()
    
    books.forEach(book => {
      // original_language is stored as a language code (e.g., "ru", "en")
      const originalLang = (book as any).original_language
      if (originalLang && typeof originalLang === 'string') {
        relevantCodes.add(originalLang)
      }
    })
    
    if (relevantCodes.size === 0) return [] // No original languages, return empty array
    
    return ALL_LANGUAGES.filter((lang: { code: string }) => relevantCodes.has(lang.code))
  }, [books])

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    loadBooks()
  }, [user, navigate, searchParams])

  // Sync state with URL parameters
  useEffect(() => {
    setSearchTerm(searchParams.get('search') || '')
    setLanguageFilter(searchParams.get('language') || '')
    setGenreFilter(searchParams.get('genre') || '')
    setOriginalLanguageFilter(searchParams.get('original_language') || '')
    const sortParam = searchParams.get('sort') || ''
    setSortFilter(sortParam as SortOption | '')
  }, [searchParams])

  const loadBooks = async () => {
    try {
      setLoading(true)
      const page = parseInt(searchParams.get('page') || '1', 10)
      const pageSize = parseInt(searchParams.get('page_size') || '24', 10)
      
      const response = await apiClient.getUserBooks({ page, page_size: pageSize })
      setBooks(response.books || [])
      setPagination(response.pagination || null)
      
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Error loading books:', error)
    } finally {
      setLoading(false)
    }
  }

  // Update URL parameters when filters change
  const updateFilters = (updates: { 
    search?: string
    language?: string
    genre?: string
    original_language?: string
    sort?: string
  }) => {
    const newParams = new URLSearchParams()
    
    const currentSearch = searchParams.get('search') || ''
    const currentLanguage = searchParams.get('language') || ''
    const currentGenre = searchParams.get('genre') || ''
    const currentOriginalLanguage = searchParams.get('original_language') || ''
    const currentSort = searchParams.get('sort') || ''
    
    const finalSearch = 'search' in updates ? updates.search || '' : currentSearch
    const finalLanguage = 'language' in updates ? updates.language || '' : currentLanguage
    const finalGenre = 'genre' in updates ? updates.genre || '' : currentGenre
    const finalOriginalLanguage = 'original_language' in updates ? updates.original_language || '' : currentOriginalLanguage
    const finalSort = 'sort' in updates ? updates.sort || '' : currentSort
    
    if (finalSearch) newParams.set('search', finalSearch)
    if (finalLanguage) newParams.set('language', finalLanguage)
    if (finalGenre) newParams.set('genre', finalGenre)
    if (finalOriginalLanguage) newParams.set('original_language', finalOriginalLanguage)
    if (finalSort) newParams.set('sort', finalSort)
    
    setSearchParams(newParams)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    updateFilters({ search: searchTerm })
  }

  const handleLanguageChange = (language: string | string[]) => {
    // Dashboard uses single-select, so language should always be a string
    const langStr = typeof language === 'string' ? language : ''
    setLanguageFilter(langStr)
    updateFilters({ language: langStr })
  }

  const handleGenreChange = (genre: string) => {
    setGenreFilter(genre)
    updateFilters({ genre })
  }

  const handleOriginalLanguageChange = (language: string) => {
    setOriginalLanguageFilter(language)
    updateFilters({ original_language: language })
  }

  const handleSortChange = (sort: SortOption | '') => {
    setSortFilter(sort)
    updateFilters({ sort })
  }

  const handleClearFilters = () => {
    setLanguageFilter('')
    setGenreFilter('')
    setOriginalLanguageFilter('')
    setSortFilter('')
    setSearchTerm('')
    setSearchParams(new URLSearchParams())
  }

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    setSearchParams(newParams)
  }

  // Filter books based on current filters
  const filteredBooks = useMemo(() => {
    let filtered = [...books]

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(book => {
        const title = (book as any).title_original || (book as any).title || ''
        const author = (book as any).author_original || (book as any).author || ''
        return title.toLowerCase().includes(searchLower) || author.toLowerCase().includes(searchLower)
      })
    }

    // Language filter
    if (languageFilter) {
      filtered = filtered.filter(book => {
        const availableLangs = (book as any).available_languages || []
        return availableLangs.includes(languageFilter)
      })
    }

    // Genre filter
    if (genreFilter) {
      filtered = filtered.filter(book => {
        return (book as any).genre === genreFilter
      })
    }

    // Original language filter
    if (originalLanguageFilter) {
      filtered = filtered.filter(book => {
        return (book as any).original_language === originalLanguageFilter
      })
    }

    // Sort
    if (sortFilter) {
      filtered.sort((a, b) => {
        switch (sortFilter) {
          case 'title-asc':
            const titleA = ((a as any).title_original || (a as any).title || '').toLowerCase()
            const titleB = ((b as any).title_original || (b as any).title || '').toLowerCase()
            return titleA.localeCompare(titleB)
          case 'title-desc':
            const titleA2 = ((a as any).title_original || (a as any).title || '').toLowerCase()
            const titleB2 = ((b as any).title_original || (b as any).title || '').toLowerCase()
            return titleB2.localeCompare(titleA2)
          case 'author-asc':
            const authorA = ((a as any).author_original || (a as any).author || '').toLowerCase()
            const authorB = ((b as any).author_original || (b as any).author || '').toLowerCase()
            return authorA.localeCompare(authorB)
          case 'author-desc':
            const authorA2 = ((a as any).author_original || (a as any).author || '').toLowerCase()
            const authorB2 = ((b as any).author_original || (b as any).author || '').toLowerCase()
            return authorB2.localeCompare(authorA2)
          case 'newest':
            const dateA = new Date((a as any).created_at || 0).getTime()
            const dateB = new Date((b as any).created_at || 0).getTime()
            return dateB - dateA
          default:
            return 0
        }
      })
    }

    return filtered
  }, [books, searchTerm, languageFilter, genreFilter, originalLanguageFilter, sortFilter])

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6">
        {/* My Graded Books Section (user's own translated books) - shown first */}
        <GradedBooksSection />

        {/* Taken from Library Section (books read from public library) */}
        <TakenBooksSection />

        {/* Legacy: Show filtered book list if searching/filtering */}
        {(searchTerm || languageFilter || genreFilter || originalLanguageFilter || sortFilter) && (
          <>
            <div className="border-t pt-6 mt-6">
              <h2 className="text-lg font-semibold mb-4">{t('dashboard.searchResults', 'Search Results')}</h2>
              
              {/* Search and Filters */}
              {books.length > 0 && (
                <SearchComponent
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  onSearchSubmit={handleSearch}
                  searchPlaceholder={t('dashboard.searchPlaceholder')}
                  selectedLanguage={languageFilter}
                  selectedGenre={genreFilter}
                  selectedOriginalLanguage={originalLanguageFilter}
                  selectedSort={sortFilter}
                  onLanguageChange={handleLanguageChange}
                  onGenreChange={handleGenreChange}
                  onOriginalLanguageChange={handleOriginalLanguageChange}
                  onSortChange={handleSortChange}
                  onApplyFilters={() => {}}
                  onClearFilters={handleClearFilters}
                  showGenreFilter={true}
                  showOriginalLanguageFilter={true}
                  showSortFilter={true}
                  useMultiSelect={false}
                  topLanguageCodes={topLanguageCodes}
                  translatedToLanguages={translatedToLanguages}
                  originalLanguages={originalLanguages}
                />
              )}

              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              )}

              {!loading && filteredBooks.length === 0 && books.length > 0 && (
                <div className="text-center py-16">
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    {t('dashboard.noMatchingBooks', 'No books match your filters')}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    {t('dashboard.tryAdjustingFilters', 'Try adjusting your search or filters to see more results.')}
                  </p>
                  <Button variant="outline" onClick={handleClearFilters}>
                    {t('dashboard.clearFilters', 'Clear All Filters')}
                  </Button>
                </div>
              )}

              {!loading && filteredBooks.length > 0 && (
                <>
                  <BookList books={filteredBooks} />
                  
                  {/* Pagination */}
                  {pagination && pagination.total_pages > 1 && (
                    <div className="mt-8 mb-4">
                      <Pagination 
                        pagination={pagination} 
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}